import React, { useState } from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";
import { getSEOData } from "../utils/seoConfig";

const Contact = () => {
  const seoData = getSEOData("contact");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    category: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("");

    try {
      // Simulate form submission
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        subject: "",
        category: "",
        message: "",
      });
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      title: "General Information",
      email: "info@exe-competition.lk",
      description: "For general inquiries about EXE competition",
      icon: "📧",
    },
    {
      
    },
  ];

  const socialLinks = [
    {
      platform: "Discord",
      url: "https://discord.gg/xauShxdx6u",
      username: "EXE Competition",
      icon: <ion-icon name="logo-discord"></ion-icon>,
    },
    {
      platform: "itch.io",
      url: "https://levelstair.itch.io/",
      username: "EXE Competition",
      icon: <ion-icon name="game-controller-outline"></ion-icon>,
    },
    {
      platform: "YouTube",
      url: "https://www.youtube.com/@LEVELSTAIR",
      username: "EXE Competition",
      icon: <ion-icon name="logo-youtube"></ion-icon>,
    },
    {
      platform: "X (Twitter)",
      url: "https://x.com/levelstair",
      username: "EXE Competition",
      icon: <ion-icon name="logo-twitter"></ion-icon>,
    },
    {
      platform: "Facebook",
      url: "https://www.facebook.com/LEVELSTAIR",
      username: "EXE Competition",
      icon: <ion-icon name="logo-facebook"></ion-icon>,
    },
    {
      platform: "Instagram",
      url: "https://instagram.com/levelstair",
      username: "@exe_competition",
      icon: <ion-icon name="logo-instagram"></ion-icon>,
    },
    {
      platform: "LinkedIn",
      url: "https://www.linkedin.com/company/levelstair/",
      username: "EXE Competition",
      icon: <ion-icon name="logo-linkedin"></ion-icon>,
    },
    {
      platform: "Reddit",
      url: "https://www.reddit.com/r/LEVELSTAIR/",
      username: "EXE Competition",
      icon: <ion-icon name="logo-reddit"></ion-icon>,
    },
  ];

  const faqs = [
    {
      question: "When is the registration deadline?",
      answer:
        "Registration closes on August 15, 2024. Make sure to register before this date to participate.",
    },
    {
      question: "Can I participate as a solo developer?",
      answer:
        "Yes! You can participate solo or as part of a team (maximum 4 members per team).",
    },
    {
      question: "What platforms should I develop for?",
      answer:
        "You can develop for any platform - PC, mobile, web, or VR/AR. Choose based on your category.",
    },
    {
      question: "Are there any age restrictions?",
      answer:
        "No age restrictions! We welcome developers of all ages, from school students to industry professionals.",
    },
  ];

  return (
    <HelmetProvider>
      <div className="exe-bg-pattern min-h-screen pt-20">
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          image={seoData.image}
          url={seoData.url}
          keywords={seoData.keywords}
        />

        <div className="exe-container py-16">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-6xl mx-auto"
          >
            {/* Header */}
            <div className="text-center mb-16">
              <h1 className="exe-title text-exe-text-accent mb-6">
                Contact Us
              </h1>
              <div className="h-1 w-24 bg-exe-gradient-primary mx-auto mb-8"></div>
              <p className="exe-text text-xl max-w-3xl mx-auto">
                Have questions about EXE competition? Need technical support?
                Want to become a partner? We're here to help!
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Form */}
              <div className="lg:col-span-2">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                  viewport={{ once: true }}
                  className="exe-glass-card p-8"
                >
                  <h2 className="exe-heading text-exe-text-accent mb-6">
                    Send us a Message
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-exe-text-primary font-semibold mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-exe-bg-secondary border border-exe-text-muted rounded-lg focus:border-exe-text-accent focus:outline-none transition-colors text-exe-text-primary"
                          placeholder="Your full name"
                        />
                      </div>

                      <div>
                        <label className="block text-exe-text-primary font-semibold mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-exe-bg-secondary border border-exe-text-muted rounded-lg focus:border-exe-text-accent focus:outline-none transition-colors text-exe-text-primary"
                          placeholder="your.email@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-exe-text-primary font-semibold mb-2">
                          Subject
                        </label>
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-exe-bg-secondary border border-exe-text-muted rounded-lg focus:border-exe-text-accent focus:outline-none transition-colors text-exe-text-primary"
                          placeholder="Brief subject line"
                        />
                      </div>

                      <div>
                        <label className="block text-exe-text-primary font-semibold mb-2">
                          Category
                        </label>
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-exe-bg-secondary border border-exe-text-muted rounded-lg focus:border-exe-text-accent focus:outline-none transition-colors text-exe-text-primary"
                        >
                          <option value="">Select category</option>
                          <option value="general">General Information</option>
                          <option value="technical">Technical Support</option>
                          <option value="partnership">Partnership</option>
                          <option value="media">Media Inquiry</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-exe-text-primary font-semibold mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 bg-exe-bg-secondary border border-exe-text-muted rounded-lg focus:border-exe-text-accent focus:outline-none transition-colors text-exe-text-primary resize-none"
                        placeholder="Tell us how we can help you..."
                      />
                    </div>

                    {submitStatus === "success" && (
                      <div className="p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
                        <p className="text-green-400">
                          Message sent successfully! We'll get back to you soon.
                        </p>
                      </div>
                    )}

                    {submitStatus === "error" && (
                      <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                        <p className="text-red-400">
                          Failed to send message. Please try again or contact us
                          directly.
                        </p>
                      </div>
                    )}

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className={`exe-btn exe-btn-primary w-full ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                      whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </motion.button>
                  </form>
                </motion.div>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                {/* Direct Contact 
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4 }}
                  viewport={{ once: true }}
                  className="exe-glass-card p-6"
                >
                  <h3 className="exe-heading text-exe-text-accent mb-6">
                    Direct Contact
                  </h3>

                  <div className="space-y-4">
                    {contactMethods.map((method, index) => (
                      <div key={method.title} className="space-y-2">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{method.icon}</span>
                          <div>
                            <h4 className="font-semibold text-exe-text-primary text-sm">
                              {method.title}
                            </h4>
                            <a
                              href={`mailto:${method.email}`}
                              className="text-exe-text-accent hover:text-exe-text-accent/80 transition-colors text-sm"
                            >
                              {method.email}
                            </a>
                            <p className="text-exe-text-muted text-xs mt-1">
                              {method.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>*/}

                {/* Social Media */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.12 }}
                  viewport={{ once: true }}
                  className="exe-glass-card p-6"
                >
                  <h3 className="exe-heading text-exe-text-accent mb-6">
                    Follow Us
                  </h3>

                  <div className="space-y-4">
                    {socialLinks.map((social, index) => (
                      <motion.a
                        key={social.platform}
                        href={social.url}
                        className="flex items-center gap-3 p-3 rounded-lg bg-exe-bg-secondary/50 hover:bg-exe-bg-secondary transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span className="text-xl">{social.icon}</span>
                        <div>
                          <div className="font-semibold text-exe-text-primary text-sm">
                            {social.platform}
                          </div>
                          <div className="text-exe-text-muted text-xs">
                            {social.username}
                          </div>
                        </div>
                      </motion.a>
                    ))}
                  </div>

                  {/* Community Action Buttons */}
                  <div className="mt-6 pt-6 border-t border-exe-text-muted/30 space-y-3">
                   
                    <motion.a
                      href="https://www.levelstair.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="exe-btn exe-btn-outline w-full text-sm"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Visit Levelstair
                    </motion.a>
                  </div>
                </motion.div>

                {/* Quick FAQ 
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.24 }}
                  viewport={{ once: true }}
                  className="exe-glass-card p-6"
                >
                  <h3 className="exe-heading text-exe-text-accent mb-6">
                    Quick FAQ
                  </h3>

                  <div className="space-y-4">
                    {faqs.map((faq, index) => (
                      <details key={index} className="group">
                        <summary className="cursor-pointer text-exe-text-primary font-semibold text-sm mb-2 hover:text-exe-text-accent transition-colors">
                          {faq.question}
                        </summary>
                        <p className="text-exe-text-muted text-xs leading-relaxed pl-4 border-l-2 border-exe-text-muted/30">
                          {faq.answer}
                        </p>
                      </details>
                    ))}
                  </div>

                  <div className="mt-6 pt-6 border-t border-exe-text-muted/30">
                    <a
                      href="/rules"
                      className="text-exe-text-accent hover:text-exe-text-accent/80 transition-colors text-sm font-semibold"
                    >
                      View All Rules & Guidelines →
                    </a>
                  </div>
                </motion.div>*/}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </HelmetProvider>
  );
};

export default Contact;
